import React from 'react';
import { resolveTheme, panelStyle, fmtBytes } from '../themes';
import type { Theme } from '../themes';
import type { CacheMetrics } from '../../domain/value-objects/CacheMetrics';
import { TOTAL_FRAMES } from '../../domain/constants';

interface CacheStatusProps {
  theme?: string | Theme;
  metrics: CacheMetrics;
  prefetchProgress?: { loaded: number; total: number } | null;
}

export function CacheStatus({ theme, metrics, prefetchProgress }: CacheStatusProps) {
  const T = resolveTheme(theme);
  const bytes = metrics.currentMb * 1024 * 1024;
  const limitBytes = metrics.limitMb * 1024 * 1024;
  const pct = Math.min(1, metrics.currentMb / metrics.limitMb);
  const warn = pct > 0.8;
  const barColor = warn ? '#ff9f43' : T.accent;
  const loadedSet = new Set(metrics.loadedFrames);

  return (
    <div
      aria-label={`Cache status: ${metrics.currentMb.toFixed(1)}MB of ${metrics.limitMb}MB`}
      style={panelStyle(T, { padding: '10px 14px', minWidth: 240 })}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{
          width: 6, height: 6, borderRadius: warn ? '1px' : '50%',
          background: barColor, boxShadow: `0 0 6px ${barColor}`,
        }} />
        <span style={{ fontWeight: T.headerWeight, fontSize: 11, textTransform: 'uppercase' }}>
          Frame Cache
        </span>
        <span style={{ marginLeft: 'auto', color: T.textDim, fontSize: 10 }}>
          {warn ? 'NEAR LIMIT' : 'OK'}
        </span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
        <span style={{ color: T.textDim }}>{fmtBytes(bytes)}</span>
        <span style={{ color: T.textDim, fontVariantNumeric: 'tabular-nums' }}>
          {(pct * 100).toFixed(0)}% / {metrics.limitMb} MB
        </span>
      </div>
      <div style={{ height: 4, background: 'rgba(127,127,127,.18)', borderRadius: 2, overflow: 'hidden', marginBottom: 8 }}>
        <div style={{
          height: '100%', width: `${pct * 100}%`, background: barColor,
          transition: 'width .2s ease, background .2s ease',
          boxShadow: `0 0 8px ${barColor}`,
        }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${TOTAL_FRAMES}, 1fr)`, gap: 1 }}>
        {Array.from({ length: TOTAL_FRAMES }, (_, i) => (
          <div key={i} style={{
            height: 8,
            background: loadedSet.has(i) ? T.accent : 'rgba(127,127,127,.16)',
            borderRadius: 1,
            boxShadow: loadedSet.has(i) ? `0 0 4px ${T.accent}` : 'none',
          }} />
        ))}
      </div>
      <div style={{ marginTop: 6, fontSize: 10, color: T.textDim }}>
        {metrics.loadedFrames.length} frame{metrics.loadedFrames.length === 1 ? '' : 's'} resident
        {prefetchProgress && ` · prefetching ${prefetchProgress.loaded}/${prefetchProgress.total}`}
      </div>
    </div>
  );
}
